import React from "react";
import { Link } from "react-router-dom";
import { ArrowRightIcon, BookOpenIcon, CalendarDaysIcon, HouseIcon, LandmarkIcon, ShieldCheckIcon, SparklesIcon, UsersIcon, UtensilsIcon, BoxIcon } from "lucide-react";
import { demoCampusSignals, demoProgrammeSignals, landingImages } from "../../data/landingVisual";
const PROGRAMME_ICONS: BoxIcon[] = [BookOpenIcon, UsersIcon, SparklesIcon, CalendarDaysIcon];
const CAMPUS_ICONS: BoxIcon[] = [HouseIcon, CalendarDaysIcon, UtensilsIcon, LandmarkIcon];
function SignalList({
  eyebrow,
  subject,
  items,
  icons
}: {
  eyebrow: string;
  subject: string;
  items: readonly {
    label: string;
    note: string;
  }[];
  icons: BoxIcon[];
}) {
  return <div className="flex h-full flex-col rounded-card border border-line bg-canvas px-5 py-5" data-id="element-2390" data-mp="234hj11tje1jc">
      <p className="text-body-sm text-ink-muted" data-id="element-2391" data-mp="1p4pysjcffoww">{eyebrow}</p>
      <p className="text-h4 font-semibold text-ink" data-id="element-2392" data-mp="1p7chnf4rpquf">{subject}</p>
      <ul className="mt-4 space-y-3" data-id="element-2393" data-mp="1t5s33el7ar5q">
        {items.map((item, index) => {
        const Icon = icons[index] ?? SparklesIcon;
        return <li key={item.label} className="flex items-start gap-2.5" data-id="element-2394" data-mp="1et91ccyj4wp4" data-mpi={index}>
              <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-brand-50" data-id="element-2395" data-mp="19ra1hd200hwx">
                <Icon aria-hidden className="h-3.5 w-3.5 text-brand-700" data-id="element-2396" data-mp="1ookj7458ik13" />
              </span>
              <span className="min-w-0" data-id="element-2397" data-mp="1jqsjvp29fvmv">
                <span className="block text-body-sm font-medium text-ink" data-id="element-2398" data-mp="w0exae2g2ozo">{item.label}</span>
                <span className="block text-caption text-ink-subtle" data-id="element-2399" data-mp="r1llcq2t361m">{item.note}</span>
              </span>
            </li>;
      })}
      </ul>
    </div>;
}

/**
 * Personalization, with its basis stated.
 *
 * Each column says why it is showing what it shows — your programme, your
 * campus — and the fourth panel gives the control rather than a reassurance.
 */
export function PersonalizationSection() {
  return <section aria-labelledby="personalization-heading" className="bg-surface py-16 lg:py-20" data-id="element-2400" data-mp="8bc4qb69vbqe">
      <div className="mx-auto max-w-7xl px-4 sm:px-6" data-id="element-2401" data-mp="xzhltd42706a">
        <h2 id="personalization-heading" className="max-w-[26ch] text-h2 font-bold tracking-tight text-ink" data-id="element-2402" data-mp="xypysqoj2arm">
          Campuzon gets <span className="text-brand-700" data-id="element-2403" data-mp="11ygn8nql0esz">smarter</span> as you do.
          <br data-id="element-2404" data-mp="16s33sqzv92u2" />
          <span className="text-ink-muted" data-id="element-2405" data-mp="16sbd68ws5q0i">Personalized for you. Built for your campus.</span>
        </h2>

        <div className="mt-9 grid gap-5 lg:grid-cols-4" data-id="element-2406" data-mp="1bsqpcx5bsrgw">
          <SignalList eyebrow="Because you’re studying" subject="Systems Engineering" items={demoProgrammeSignals} icons={PROGRAMME_ICONS} data-id="element-2407" data-mp="f1f2ney6zxge" />

          <div className="overflow-hidden rounded-card border border-line" data-id="element-2408" data-mp="j41q73ntyw7s">
            <img src={landingImages.personalization} alt="A university student working on a laptop" loading="lazy" className="h-full min-h-[16rem] w-full object-cover" data-id="element-2409" data-mp="nzh1poyd8g11" />
          </div>

          <SignalList eyebrow="Because you’re at" subject="UNILAG" items={demoCampusSignals} icons={CAMPUS_ICONS} data-id="element-2410" data-mp="1zfffg64hnq8l" />

          <div className="flex h-full flex-col rounded-card border border-brand-100 bg-brand-50 px-5 py-5" data-id="element-2411" data-mp="24dgwymm9nhhy">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-surface" data-id="element-2412" data-mp="3ozf8vc5dtpxb">
              <ShieldCheckIcon aria-hidden className="h-5 w-5 text-brand-700" data-id="element-2413" data-mp="3k0cfioyrl856" />
            </span>
            <p className="mt-4 text-h4 font-semibold text-ink" data-id="element-2414" data-mp="3f5pzg8hqmrer">Your privacy matters</p>
            <p className="mt-2 max-w-[34ch] text-body-sm leading-6 text-ink" data-id="element-2415" data-mp="3a7wvlm3wq9hn">
              Suggestions come from your programme, your campus and what you have chosen to do —
              never from your notes, diary, results, messages or balances.
            </p>
            <p className="mt-2.5 max-w-[34ch] text-caption text-ink-muted" data-id="element-2416" data-mp="2w7vm5i9kttqk">
              Every signal is listed in your settings, and switching one off changes what you see
              immediately.
            </p>
            <div className="mt-auto pt-5" data-id="element-2417" data-mp="ug0pa8rzbtse">
              <Link to="/settings" className="group inline-flex min-h-touch items-center gap-1.5 text-body-sm font-semibold text-brand-700 hover:text-brand-800" data-id="element-2418" data-mp="uexye0unprch">
                See what personalizes your experience
                <ArrowRightIcon aria-hidden className="h-4 w-4 transition-transform duration-150 ease-out group-hover:translate-x-1" data-id="element-2419" data-mp="12hq3wh56no6r" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>;
}